import clamp from 'clamp';
import * as React from 'react';
import type { LayoutRectangle, StyleProp, View, ViewStyle } from 'react-native';

import { assertIsDefined, assertNever } from '@gitbook/util-ts';

import { spacing } from '../../constants';
import {
    type PopoverAlignment,
    type PopoverDimensionsRange,
    type PopoverPlacement,
    type PopoverPositionOptions,
    usePopoverPosition,
} from '../../Popover';
import type { LayoutDimensions } from '../../Responsive';
import { measureNodeInWindow } from '../../util';
import {
    type PaletteAnchorRef,
    type PaletteDef,
    PalettePositionMaxHeight,
    type PalettePositionOptions,
    PalettePositionWidth,
} from '../types';

/** When using PalettePositionMaxHeight.AUTO, layout logic and async processes within the items would cause differences
 * in calculated vs actual height. This buffer gives us some leeway, as we'd rather overestimate (which isnt very
 * problematic) opposed to underestimating which was causing minor overflows, aka tiny scrollbars.
 */
const AUTO_HEIGHT_BUFFER = spacing.small;

const PALETTE_WIDTH_SMALL = 204;
const PALETTE_WIDTH_MEDIUM = 246;
const PALETTE_WIDTH_LARGE = 384;
const PALETTE_WIDTH_XLARGE = 800;
const PALETTE_WIDTH_LINKS = 500;
const PALETTE_MAX_WIDTH_ANCHOR = 630;

const PALETTE_MAX_HEIGHT_MD = 355;
const PALETTE_MAX_HEIGHT_LG = 450;

/**
 * Compute the live position of the palette on the screen when inlined.
 */
export function usePalettePosition(props: {
    anchorRef: PaletteAnchorRef;
    popoverRef: React.RefObject<View>;
    palette: PaletteDef;
    parentPlacement: PopoverPlacement | undefined;
    preferredPlacement: PopoverPlacement | undefined;
}): [StyleProp<ViewStyle>, PopoverPlacement, PopoverAlignment] {
    const { anchorRef, popoverRef, palette, parentPlacement, preferredPlacement } = props;

    const hasParent = !!palette.parent;

    const options = React.useMemo(() => {
        const options: Partial<PopoverPositionOptions> = {
            cover: palette.position?.cover,
            spacingWithAnchor: spacing.small,
            preferredPlacement,
        };

        if (hasParent) {
            // Adjust position to align with top of parent palette
            options.offset = {
                top: -spacing.xsmall,
                left: -spacing.xsmall,
                right: -spacing.xsmall,
            };
            // And to compensate for the parent palette's padding
            options.spacingWithAnchor = spacing.medium;
            // If parent is placed on the left, we want to priorize this direction as well
            // otherwise we default to the right
            options.allowedPlacements =
                parentPlacement === 'left' ? ['left', 'right'] : ['right', 'left'];
        } else if (palette.position?.width === PalettePositionWidth.ANCHOR) {
            options.allowedPlacements = ['bottom', 'top'];
        } else {
            options.spacingWithWindow = spacing.small;
        }

        return options;
    }, [
        palette.position?.cover,
        palette.position?.width,
        preferredPlacement,
        hasParent,
        parentPlacement,
        anchorRef,
    ]);

    const [positionStyle, placement, alignment, updatePosition] = usePopoverPosition(
        anchorRef,
        (anchorRect) => {
            return getPalettePositionRectFromAnchor(anchorRect, popoverRef, palette.position);
        },
        options
    );

    // Update the position when the position options changed.
    React.useLayoutEffect(() => {
        updatePosition();
    }, [updatePosition, options]);

    return [positionStyle, placement, alignment];
}

function getPalettePositionRectFromAnchor(
    anchorRect: LayoutRectangle,
    popoverRef: React.RefObject<View>,
    position: PalettePositionOptions | undefined
): LayoutDimensions & Partial<PopoverDimensionsRange> {
    const widthType = position?.width || PalettePositionWidth.AUTO;
    const maxHeightType = position?.maxHeight || PalettePositionMaxHeight.FIXED;

    assertIsDefined(popoverRef.current);
    const dimensions = measureNodeInWindow(popoverRef.current);

    const height = dimensions.height;
    const maxHeight =
        maxHeightType === PalettePositionMaxHeight.AUTO
            ? height + AUTO_HEIGHT_BUFFER
            : PALETTE_MAX_HEIGHT_MD;

    switch (widthType) {
        case PalettePositionWidth.SMALL:
            return {
                width: PALETTE_WIDTH_SMALL,
                maxWidth: PALETTE_WIDTH_SMALL,
                minWidth: PALETTE_WIDTH_SMALL,
                minHeight: 0,
                height,
                maxHeight,
            };
        case PalettePositionWidth.MEDIUM:
            return {
                width: PALETTE_WIDTH_MEDIUM,
                maxWidth: PALETTE_WIDTH_MEDIUM,
                minWidth: PALETTE_WIDTH_MEDIUM,
                minHeight: 0,
                height,
                maxHeight,
            };
        case PalettePositionWidth.LARGE:
            return {
                width: PALETTE_WIDTH_LARGE,
                maxWidth: PALETTE_WIDTH_LARGE,
                minWidth: PALETTE_WIDTH_LARGE,
                minHeight: 0,
                height,
                maxHeight,
            };
        case PalettePositionWidth.XLARGE:
            return {
                width: document.documentElement.clientWidth,
                maxWidth: PALETTE_WIDTH_XLARGE,
                minWidth: PALETTE_WIDTH_XLARGE,
                minHeight: 0,
                height,
                maxHeight,
            };
        case PalettePositionWidth.ANCHOR: {
            // Optimize to try to size just like the anchor
            // but also to not go to small (>PALETTE_WIDTH_MEDIUM)
            // or too big (<PALETTE_MAX_WIDTH_ANCHOR)
            const minWidth = Math.max(PALETTE_WIDTH_MEDIUM, anchorRect.width);
            const maxWidth = clamp(anchorRect.width, minWidth, PALETTE_MAX_WIDTH_ANCHOR);
            const width = clamp(anchorRect.width, minWidth, maxWidth);
            return {
                width,
                maxWidth,
                minWidth,
                minHeight: 0,
                height,
                maxHeight,
            };
        }
        case PalettePositionWidth.AUTO: {
            return {
                width: Math.max(dimensions.width, PALETTE_WIDTH_SMALL),
                maxWidth: PALETTE_WIDTH_XLARGE,
                minWidth: PALETTE_WIDTH_SMALL,
                minHeight: 0,
                height,
                maxHeight,
            };
        }
        case PalettePositionWidth.LINKS: {
            return {
                width: PALETTE_WIDTH_LINKS,
                maxWidth: PALETTE_WIDTH_LINKS,
                minWidth: PALETTE_WIDTH_LINKS,
                minHeight: 0,
                height: dimensions.height || PALETTE_MAX_HEIGHT_LG,
                maxHeight: PALETTE_MAX_HEIGHT_LG,
            };
        }
        case PalettePositionWidth.ELEMENT: {
            return {
                width: dimensions.width,
                height: dimensions.height,
            };
        }
        default:
            assertNever(widthType);
    }
}
